<script>
import {useI18n} from "vue-i18n";
import DragNDrop from "../brand/dragNDrop.vue";
import AccordionItem from "../../components/accordion/AccordionItem.vue";
import {usePaymentProcessorStore} from "@/javascript/stores/paymentProcessors";
import {useOrganizationStore} from "@/javascript/stores/organizations";
import {ApiService} from "@/javascript/services/api.service";

/*
* Repayment Form for those Using Helcim
* Broken down into 3 months
* Each Quarter users have to submit this to get reimbursed
* */

export default {
  name: "RepaymentProcess",
  components: {AccordionItem, DragNDrop},
  setup() {
    const {t} = useI18n();
    const processorStore = usePaymentProcessorStore();
    const orgStore = useOrganizationStore();
    return {t, processorStore, orgStore};
  },
  data() {
    return {
      windowLoading: true,
      windowOpen: false,
      windowInfo: {},
      year: new Date().getFullYear(),
      month1: {
        totalProcessingFees: '',
        recurringPaymentFees: '',
        file: null,
        monthName: '',
      },
      month2: {
        totalProcessingFees: '',
        recurringPaymentFees: '',
        file: null,
        monthName: '',
      },
      month3: {
        totalProcessingFees: '',
        recurringPaymentFees: '',
        file: null,
        monthName: '',
      },
      uploadedFiles: [],
      parsingFile: false,
      fileParsed: false,
      tempRecords: [],
      fileKey: 0,
      submitting: false,
      submitSuccess: false,
      submitError: '',
      serverErrors: {},
      touched: {
        month1: { totalProcessingFees: false, recurringPaymentFees: false, file: false, monthName: false },
        month2: { totalProcessingFees: false, recurringPaymentFees: false, file: false, monthName: false },
        month3: { totalProcessingFees: false, recurringPaymentFees: false, file: false , monthName: false}
      }
    };
  },
  computed: {
    currencySymbol() {
      return this.orgStore.organization?.currency === 'CAD' ? 'CA$' : '$';
    },
    currencyCode() {
      return this.orgStore.organization?.currency || 'USD';
    },
    totalReimbursementRequested() {
      const month1Total = this.month1.totalProcessingFees|| 0;
      const month1Recurring = this.month1.recurringPaymentFees || 0;
      const month2Total = this.month2.totalProcessingFees || 0;
      const month2Recurring = this.month2.recurringPaymentFees || 0;
      const month3Total = this.month3.totalProcessingFees || 0;
      const month3Recurring = this.month3.recurringPaymentFees || 0;

      const total = month1Total + month1Recurring + month2Total + month2Recurring + month3Total + month3Recurring;
      return total > 0 ? total.toFixed(2) : '';
    },
    isFormValid() {
      // Checking that at least 1 month is completely filled out
      const atLeastOneComplete = ['month1', 'month2', 'month3'].some(key =>
        this[key].totalProcessingFees !== '' &&
        this[key].recurringPaymentFees !== '' &&
        this[key].file &&
        this[key].monthName !== ''
      );
      const noPartialMonths = this.getPartialMonths.length === 0;
      return atLeastOneComplete && noPartialMonths;
    },
    // Check if a month has some data in it (Partially filled out)
    // This puts those months into an array
    getPartialMonths(){
      return ['month1', 'month2', 'month3'].filter(key => {
        const hasSomeData = this[key].totalProcessingFees !== '' ||
                            this[key].recurringPaymentFees !== '' ||
                            this[key].file ||
                            this[key].monthName;
        const isComplete = this[key].totalProcessingFees !== '' &&
                           this[key].recurringPaymentFees !== '' &&
                           this[key].file &&
                          this[key].monthName;
        return hasSomeData && !isComplete;
      });
    },
    monthOptions(){
      // When window is open, only show the months for that quarter
      if (this.windowInfo.months && this.windowInfo.months.length > 0) {
        return this.windowInfo.months;
      }
      return ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
    },
    yearOptions(){
      const currentYear = new Date().getFullYear();
      return [currentYear - 1, currentYear];
    },
    nextWindowFormatted() {
      if (!this.windowInfo.next_window_date) return '';
      const date = new Date(this.windowInfo.next_window_date + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    },
    windowEndFormatted() {
      if (!this.windowInfo.window_end) return '';
      // Show the last day of the window (day before close date) to avoid ambiguity
      const closeDate = new Date(this.windowInfo.window_end + 'T00:00:00');
      closeDate.setDate(closeDate.getDate() - 1);
      return closeDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
  },
  async mounted() {
    await this.fetchWindowStatus();
  },
  methods: {
    async fetchWindowStatus() {
      try {
        const api = new ApiService();
        let url = `/organizations/${this.processorStore.organizationId}/processor/repayment_submissions/window_status`;

        // Allow QA to test with a simulated date (non-production only)
        const urlParams = new URLSearchParams(window.location.search);
        const testDate = urlParams.get('test_date');
        if (testDate) {
          url += `?test_date=${testDate}`;
        }

        const response = await api.get(url);
        this.windowInfo = response.json || response;
        this.windowOpen = this.windowInfo.open;
        // Auto-set year from backend
        if (this.windowInfo.quarter_year) {
          this.year = this.windowInfo.quarter_year;
        }
      } catch (e) {
        console.error("Error fetching repayment window status:", e);
        this.windowOpen = false;
      } finally {
        this.windowLoading = false;
      }
    },
    availableMonthOptions(currentIndex) {
      const allMonths = this.monthOptions;
      const selected = ['month1', 'month2', 'month3']
        .filter((_, i) => i !== currentIndex)
        .map(key => this[key].monthName)
        .filter(name => name !== '');
      return allMonths.filter(m => !selected.includes(m));
    },
    sanitizeFee(monthKey, field) {
      let val = String(this[monthKey][field]);
      // Strip anything beyond 2 decimal places
      const match = val.match(/^-?\d*\.?\d{0,2}/);
      val = match ? match[0] : '';
      // Clamp to 0–999999.99
      const num = parseFloat(val);
      if (!isNaN(num)) {
        this[monthKey][field] = Math.min(Math.max(num, 0), 999999.99);
      }
    },

    handleFileSelected(files, monthIndex) {
      const monthKey = `month${monthIndex + 1}`;
      if (files && Array.isArray(files)) {
        this[monthKey].file = files[0];
      } else if (files) {
        this[monthKey].file = files;
      } else {
        this[monthKey].file = null;
      }
    },

    showError(monthKey, field) {
      const serverKey = `${monthKey}.${field}`;
      return (this.touched[monthKey][field] && (!this[monthKey][field] || this[monthKey][field] === '')) || this.serverErrors[serverKey];
    },

    fieldError(monthKey, field) {
      return this.serverErrors[`${monthKey}.${field}`] || '';
    },

    resetForm() {
      ['month1', 'month2', 'month3'].forEach(key => {
        this[key].totalProcessingFees = '';
        this[key].recurringPaymentFees = '';
        this[key].file = null;
        this[key].monthName = '';
        this.touched[key] = { totalProcessingFees: false, recurringPaymentFees: false, file: false, monthName: false };
      });
      this.serverErrors = {};
      this.fileKey++;
    },

    async submitForm() {
      ['month1', 'month2', 'month3'].forEach(monthKey => {
        const hasSomeData = this[monthKey].totalProcessingFees !== '' ||
          this[monthKey].recurringPaymentFees !== '' ||
          this[monthKey].file ||
          this[monthKey].monthName !== '';
        if (hasSomeData) {
          this.touched[monthKey].totalProcessingFees = true;
          this.touched[monthKey].recurringPaymentFees = true;
          this.touched[monthKey].file = true;
          this.touched[monthKey].monthName = true;
        }
      });

      if (!this.isFormValid) return;

      this.submitting = true;
      this.submitSuccess = false;
      this.submitError = '';
      this.serverErrors = {};

      const formData = new FormData();
      // 1 month will for sure be filled out to append that
      formData.append('month_1_total_processing_fees', this.month1.totalProcessingFees);
      formData.append('month_1_recurring_payment_fees', this.month1.recurringPaymentFees);
      formData.append('month_1_document', this.month1.file);
      formData.append('year', this.year);
      formData.append('month_1_name', this.month1.monthName);
      // Check if Month2 and 3 are filled out. If so, append
      if (this.month2.totalProcessingFees !== '' || this.month2.recurringPaymentFees !== '' || this.month2.file || this.month2.monthName !== '') {
        formData.append('month_2_total_processing_fees', this.month2.totalProcessingFees);
        formData.append('month_2_recurring_payment_fees', this.month2.recurringPaymentFees);
        formData.append('month_2_document', this.month2.file);
        formData.append('month_2_name', this.month2.monthName);
       }
      if (this.month3.totalProcessingFees !== '' || this.month3.recurringPaymentFees !== '' || this.month3.file || this.month3.monthName !== '') {
        formData.append('month_3_total_processing_fees', this.month3.totalProcessingFees);
        formData.append('month_3_recurring_payment_fees', this.month3.recurringPaymentFees);
        formData.append('month_3_document', this.month3.file);
        formData.append('month_3_name', this.month3.monthName);
      }

      try {
        const api = new ApiService();
        const url = `/organizations/${this.processorStore.organizationId}/processor/repayment_submissions`;
        const response = await api.upload(url, formData, 'POST');

        if (response.status === 201) {
          this.submitSuccess = true;
          this.resetForm();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          this.parseServerErrors(response.json);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (e) {
        this.submitError = 'An unexpected error occurred. Please try again.';
      } finally {
        this.submitting = false;
      }
    },

    parseServerErrors(json) {
      if (json?.errors && Array.isArray(json.errors)) {
        this.submitError = json.errors.join('. ');
        this.serverErrors = this.mapErrorsToFields(json.errors);
      } else if (json?.error) {
        this.submitError = json.error;
      } else {
        this.submitError = 'Submission failed. Please review the form and try again.';
      }
    },

    mapErrorsToFields(errors) {
      const fieldMap = {};
      const mappings = {
        'month 1 total processing fees': 'month1.totalProcessingFees',
        'month 2 total processing fees': 'month2.totalProcessingFees',
        'month 3 total processing fees': 'month3.totalProcessingFees',
        'month 1 recurring payment fees': 'month1.recurringPaymentFees',
        'month 2 recurring payment fees': 'month2.recurringPaymentFees',
        'month 3 recurring payment fees': 'month3.recurringPaymentFees',
        'month 1 document': 'month1.file',
        'month 2 document': 'month2.file',
        'month 3 document': 'month3.file',
      };
      errors.forEach(msg => {
        const lower = msg.toLowerCase();
        for (const [key, field] of Object.entries(mappings)) {
          if (lower.includes(key)) {
            fieldMap[field] = msg;
          }
        }
      });
      return fieldMap;
    }
  }
};
</script>

<template>
  <div class="p-6 overflow-hidden">
    <!-- Loading State -->
    <div
      v-if="windowLoading"
      class="flex items-center justify-center py-12"
    >
      <p class="text-gray-500">Loading submission window status...</p>
    </div>

    <!-- Window Closed State -->
    <div
      v-else-if="!windowOpen"
      class="w-full flex items-center justify-center min-h-[300px]"
    >
      <div class="text-center max-w-md">
        <i class="fa-solid fa-calendar-xmark text-4xl mb-4 text-gray-400" />
        <h2 class="text-xl font-semibold mb-3">Submission Window Closed</h2>
        <p class="text-gray-600 mb-2">
          The repayment submission window is currently closed.
        </p>
        <p class="text-gray-600 mb-4">
          The next window will open on <strong>{{ nextWindowFormatted }}</strong>.
        </p>
        <p class="text-gray-500 text-sm">
          If you have any questions, feel free to reach out to us at <a href="mailto:help@allgive.online" class="text-green-800 underline hover:text-green-600">help@allgive.online</a>.
        </p>
      </div>
    </div>

    <!-- Window Open — Show Form -->
    <div
      v-else
      class="w-full"
    >
      <!-- Success Banner -->
      <div
        v-if="submitSuccess"
        class="mb-4 p-4 bg-green-100 border border-green-400 text-green-800 rounded-md flex justify-between items-center"
      >
        <span>Your repayment documents have been submitted successfully.</span>
        <button
          class="text-green-800 font-bold ml-4"
          @click="submitSuccess = false"
        >
          &times;
        </button>
      </div>

      <!-- Error Banner -->
      <div
        v-if="submitError"
        class="mb-4 p-4 bg-red-100 border border-red-400 text-red-800 rounded-md flex justify-between items-start"
      >
        <div>
          <p class="font-semibold">
            Submission could not be completed
          </p>
          <p class="text-sm mt-1">
            {{ submitError }}
          </p>
        </div>
        <button
          class="text-red-800 font-bold ml-4"
          @click="submitError = ''"
        >
          &times;
        </button>
      </div>

      <!-- Steps Labels -->
      <div class="flex items-baseline justify-between">
        <h1 class="text-2xl mb-2 pb-2">
          AllGive Repayment Submission
        </h1>
        <p class="text-lg text-gray-500">Open through {{ windowEndFormatted }}</p>
      </div>

      <p class="text-lg">
        Step 1 - Download Documents from Helcim
      </p>
      <div class="divider" />
      <p class="text-lg">
        Download your Helcim transaction reports from the past three months.
      </p>
      <br>
      <p class="text-lg">
        Step 2 - Please fill out form and upload documents below
      </p>
      <div class="divider" />
    <br>
    <div class="page-layout">
      <!-- Reference image (above form on small screens) -->
      <div class="reference-sidebar">
        <div class="reference-image-placeholder">
          <img
            src="@/assets/images/helcimexample.png"
            alt="Helcim Example Image"
          >
        </div>
        <p class="text-sm text-gray-600 mt-4">
          Use the highlighted fields from your Helcim transaction report to fill in the Total Processing Fees and Recurring Payment Fees for each month.
        </p>
      </div>

      <!-- Form -->
      <div class="repayment-wrapper">
        <div class="year-selector">
          <label
            for="yearSelect"
            class="text-sm font-semibold"
          >Select a Year</label>
          <select
            id="yearSelect"
            v-model="year"
            class="form-select"
          >
            <option
              v-for="y in yearOptions"
              :key="y"
              :value="y"
            >
              {{ y }}
            </option>
          </select>
        </div>
        <form
          class="space-y-6"
          @submit.prevent="submitForm"
        >
          <!-- Month cards -->
          <div
            v-for="(month, index) in [month1, month2, month3]"
            :key="index"
            class="month-card"
          >
            <div class="month-card-header">
              <p class="text-xl font-bold">
                Month {{ index + 1 }}
              </p>
              <select
                v-model="month.monthName"
                class="form-select"
              >
                <option
                  value=""
                  disabled
                >
                  Select a month
                </option>
                <option
                  v-for="option in availableMonthOptions(index)"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
            </div>
            <p
              v-if="showError('month' + (index + 1), 'monthName')"
              class="text-red-500 text-xs mb-2 text-right"
            >
              Month selection is required
            </p>

            <div class="month-layout">
              <div class="month-inputs">
                <!--     Processing Fees Field -->
                <div class="form-group">
                  <label
                    :for="'month' + (index + 1) + 'TotalProcessingFees'"
                    class="block text-sm font-semibold mb-2"
                  >
                    Total Processing Fees <span
                      v-if="index === 0"
                      class="text-red-500"
                    >*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-prefix">{{ currencySymbol }}</span>
                    <input
                      :id="'month' + (index + 1) + 'TotalProcessingFees'"
                      v-model="month.totalProcessingFees"
                      type="number"
                      step="0.01"
                      min="0.00"
                      max="999999.99"
                      class="input-group-field"
                      @input="sanitizeFee('month' + (index + 1), 'totalProcessingFees')"
                    >
                    <span class="input-group-suffix">{{ currencyCode }}</span>
                  </div>
                  <p
                    v-if="showError('month' + (index + 1), 'totalProcessingFees')"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ fieldError('month' + (index + 1), 'totalProcessingFees') || 'Total Processing Fees is required' }}
                  </p>
                </div>
                <!--    Recurring Fees Field -->
                <div class="form-group">
                  <label
                    :for="'month' + (index + 1) + 'RecurringPaymentFees'"
                    class="block text-sm font-semibold mb-2"
                  >
                    Recurring Payment Fees <span
                      v-if="index === 0"
                      class="text-red-500"
                    >*</span>
                  </label>
                  <div class="input-group">
                    <span class="input-group-prefix">{{ currencySymbol }}</span>
                    <input
                      :id="'month' + (index + 1) + 'RecurringPaymentFees'"
                      v-model="month.recurringPaymentFees"
                      type="number"
                      step="0.01"
                      min="0.00"
                      max="999999.99"
                      class="input-group-field"
                      @input="sanitizeFee('month' + (index + 1), 'recurringPaymentFees')"
                    >
                    <span class="input-group-suffix">{{ currencyCode }}</span>
                  </div>
                  <p
                    v-if="showError('month' + (index + 1), 'recurringPaymentFees')"
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ fieldError('month' + (index + 1), 'recurringPaymentFees') || 'Recurring Payment Fees is required' }}
                  </p>
                </div>
              </div>
              <!--Documentation Uploading -->
              <div class="month-upload">
                <drag-n-drop
                  :key="'month' + (index + 1) + '-' + fileKey.toString()"
                  :validate="false"
                  file-type="pdf"
                  accept=".pdf,.doc,.docx"
                  size="small"
                  :max-file-size="10 * 1024 * 1024"
                  @file-selected="(files) => handleFileSelected(files, index)"
                />
                <p
                  v-if="showError('month' + (index + 1), 'file')"
                  class="text-red-500 text-xs mt-1 text-center"
                >
                  {{ fieldError('month' + (index + 1), 'file') || 'Document upload is required' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Total Reimbursement Field -->
          <div class="form-group">
            <label
              for="totalReimbursement"
              class="text-sm font-bold mb-2"
            >
              Total Reimbursement Requested
            </label>
            <div class="input-group input-group-readonly">
              <span class="input-group-prefix">{{ currencySymbol }}</span>
              <input
                id="totalReimbursement"
                :value="totalReimbursementRequested"
                type="text"
                readonly
                class="input-group-field"
                placeholder="0.00"
              >
              <span class="input-group-suffix">{{ currencyCode }}</span>
            </div>
          </div>

          <button
            type="submit"
            class="submit-btn button-filled"
            :disabled="submitting"
          >
            {{ submitting ? 'Submitting...' : 'Submit' }}
          </button>
        </form>
      </div>
    </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-layout {
  @apply flex flex-col lg:grid lg:grid-cols-[1fr_500px] gap-8;
  max-width: 1280px;
}

.reference-sidebar {
  @apply order-first lg:order-last lg:sticky lg:top-6 lg:self-start;
}

.reference-image-placeholder {
  @apply flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg;
  aspect-ratio: 4 / 3;
  width: 100%;
  max-width: 500px;

  img {
    max-width: 100%;
    height: auto;
  }
}

.repayment-wrapper {
  max-width: 960px;
}

.month-card {
  @apply p-6 border border-gray-200 rounded-lg;
}

.month-card-header {
  @apply flex items-center justify-between mb-4;
}

.year-selector {
  @apply flex flex-row items-center justify-end gap-2 mb-4;
}

.form-select {
  @apply px-4 py-2 text-sm;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  outline: none;
  background-color: white;
  min-width: 160px;

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: -1px;
  }
}

.month-layout {
  @apply grid grid-cols-1 md:grid-cols-2 gap-6;
}

.month-inputs {
  @apply flex flex-col gap-4;
}

.month-upload {
  @apply flex flex-col items-center justify-center;
}

.divider {
  @apply bg-slate-300 w-full;
  height: 1px;
}

.submit-btn {
  @apply w-full;
  text-align: center !important;
  display: block;
  outline: none;
  padding: 0.5rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
}

.form-input {
  &:focus {
    border-color: #3b82f6;
  }
}

.input-group {
  @apply flex;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;

  &:focus-within {
    outline: 2px solid #3b82f6;
    outline-offset: -1px;
  }
}

.input-group-prefix,
.input-group-suffix {
  @apply flex items-center px-3 bg-gray-100 text-gray-500 text-sm;
  white-space: nowrap;
}

.input-group-field {
  @apply flex-1 px-4 py-2;
  border: none;
  border-radius: 0;
  outline: none;
  min-width: 0;
  margin: 0;
}

.input-group-readonly {
  @apply bg-gray-100 opacity-75;
  cursor: not-allowed;

  .input-group-field {
    @apply bg-gray-100 text-gray-500;
    cursor: not-allowed;
  }
}
</style>
